import ExcelJS from 'exceljs';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const FONT_FILE = 'NotoSansArabic-Regular.ttf';
const FONT_FAMILY = 'NotoSansArabic';

/** Navy header aligned with dashboard tables (#1e3a5f). */
const EXCEL_HEADER_FILL = 'FF1E3A5F';
const EXCEL_HEADER_FONT = 'FFFFFFFF';
const EXCEL_ROW_ALT_FILL = 'FFF1F5F9';
const EXCEL_BORDER = 'FFCBD5E1';

let notoSansArabicBase64: string | null | undefined;

function sanitizeFilename(s: string): string {
  return s
    .replace(/[^\w.-]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '')
    .slice(0, 120);
}

/** Local date-time for download names, e.g. `20260513_143052` (filesystem-safe). */
function filenameTimestamp(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}_${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
}

function sanitizeSheetName(s: string): string {
  const t = s.replace(/[[\]*?:/\\]/g, '_').slice(0, 31);
  return t.length > 0 ? t : 'Export';
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]!);
  }
  return btoa(binary);
}

async function loadNotoSansArabicBase64(): Promise<string | null> {
  if (notoSansArabicBase64 === null) return null;
  if (notoSansArabicBase64 !== undefined) return notoSansArabicBase64;
  try {
    const url = `${import.meta.env.BASE_URL}fonts/${FONT_FILE}`;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`Font HTTP ${res.status}`);
    notoSansArabicBase64 = arrayBufferToBase64(await res.arrayBuffer());
    return notoSansArabicBase64;
  } catch {
    notoSansArabicBase64 = null;
    return null;
  }
}

/** Register Noto Sans Arabic on this jsPDF instance (required per document). */
async function registerArabicFont(doc: jsPDF): Promise<boolean> {
  const b64 = await loadNotoSansArabicBase64();
  if (!b64) return false;
  doc.addFileToVFS(FONT_FILE, b64);
  doc.addFont(FONT_FILE, FONT_FAMILY, 'normal');
  doc.setFont(FONT_FAMILY, 'normal');
  return true;
}

const thinBorder: Partial<ExcelJS.Borders> = {
  top: { style: 'thin', color: { argb: EXCEL_BORDER } },
  left: { style: 'thin', color: { argb: EXCEL_BORDER } },
  bottom: { style: 'thin', color: { argb: EXCEL_BORDER } },
  right: { style: 'thin', color: { argb: EXCEL_BORDER } },
};

export async function downloadStringMatrixAsXlsx(
  fileBasename: string,
  sheetName: string,
  headers: string[],
  rows: string[][]
): Promise<void> {
  const wb = new ExcelJS.Workbook();
  wb.creator = 'Mehe Reporting';
  const ws = wb.addWorksheet(sanitizeSheetName(sheetName), {
    views: [{ state: 'frozen', ySplit: 1 }],
  });

  const headerRow = ws.addRow(headers);
  headerRow.height = 24;
  headerRow.eachCell((cell) => {
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: EXCEL_HEADER_FILL },
    };
    cell.font = {
      bold: true,
      color: { argb: EXCEL_HEADER_FONT },
      size: 11,
      name: 'Calibri',
    };
    cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
    cell.border = thinBorder as ExcelJS.Borders;
  });

  rows.forEach((data, rowIdx) => {
    const r = ws.addRow(data);
    r.eachCell((cell) => {
      cell.border = thinBorder as ExcelJS.Borders;
      cell.alignment = { vertical: 'middle', wrapText: true };
      if (rowIdx % 2 === 1) {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: EXCEL_ROW_ALT_FILL },
        };
      }
    });
  });

  ws.columns.forEach((column, i) => {
    if (column == null) return;
    let maxLen = (headers[i] ?? '').length;
    column.eachCell?.({ includeEmpty: true }, (cell) => {
      const len = (cell.value?.toString() ?? '').length;
      if (len > maxLen) maxLen = len;
    });
    column.width = Math.min(52, Math.max(12, Math.ceil(maxLen * 1.05 + 2)));
  });

  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${sanitizeFilename(`${fileBasename}_${filenameTimestamp(new Date())}`)}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
}

export type StringMatrixPdfOptions = {
  /**
   * 0-based column indices that may contain Arabic script. Those cells use the embedded Arabic font;
   * all other cells use Helvetica so digits, `$`, and Latin labels render reliably (Noto Sans Arabic omits many Latin glyphs).
   */
  arabicColumnIndices?: number[];
};

export async function downloadStringMatrixAsPdf(
  fileBasename: string,
  subtitleLines: string[],
  headers: string[],
  rows: string[][],
  options?: StringMatrixPdfOptions
): Promise<void> {
  const arabicCols = options?.arabicColumnIndices ?? [0, 2];
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const fontOk = await registerArabicFont(doc);

  const marginX = 6;
  const pageW = doc.internal.pageSize.getWidth();
  /** Full usable width so the table scales to one page (no horizontal page break). */
  const tableWidth = pageW - marginX * 2;
  const textW = tableWidth;

  const latinFont = 'helvetica' as const;
  const arabicFont = (fontOk ? FONT_FAMILY : latinFont) as string;

  let y = marginX;
  const lines = subtitleLines.filter((l) => l.trim().length > 0);
  if (lines.length > 0) {
    const [titleLine, ...detailLines] = lines;
    doc.setFont(latinFont, 'bold');
    doc.setFontSize(12);
    const titleParts = doc.splitTextToSize(titleLine!.trim(), textW);
    doc.text(titleParts, marginX, y);
    y += titleParts.length * 5.2 + 2;

    doc.setFont(latinFont, 'normal');
    doc.setFontSize(9.5);
    for (const line of detailLines) {
      const parts = doc.splitTextToSize(line.trim(), textW);
      doc.text(parts, marginX, y);
      y += parts.length * 4.6 + 1.2;
    }
  }

  autoTable(doc, {
    head: [headers],
    body: rows,
    startY: y + 4,
    tableWidth,
    styles: {
      font: latinFont,
      fontSize: 7,
      cellPadding: 0.55,
      overflow: 'linebreak',
      textColor: [15, 23, 42],
      minCellHeight: 4,
      valign: 'middle',
    },
    headStyles: {
      font: latinFont,
      fontStyle: 'bold',
      fontSize: 6.5,
      fillColor: [30, 58, 95],
      textColor: [255, 255, 255],
      halign: 'center',
      valign: 'middle',
      cellPadding: 0.45,
    },
    didParseCell: (data) => {
      const col = data.column.index;
      if (data.section === 'head') {
        data.cell.styles.font = latinFont;
        return;
      }
      if (data.section === 'body') {
        data.cell.styles.font = arabicCols.includes(col) ? arabicFont : latinFont;
      }
    },
    showHead: 'everyPage',
    horizontalPageBreak: false,
    margin: { left: marginX, right: marginX, top: marginX, bottom: marginX },
  });
  doc.save(`${sanitizeFilename(`${fileBasename}_${filenameTimestamp(new Date())}`)}.pdf`);
}
